/**
 * Client for the Exebrain Training Data Upload API, `POST /game_recording` (external.yaml v2.0.0).
 *
 * One request uploads one session ZIP with exactly six multipart fields: validation_program, module_name,
 * module_type, subject_id, upload_timestamp and data. The server stores the original bytes; resending the
 * same program/module/type/subject and ZIP bytes returns the original receipt, so a retry is always safe.
 * The API has no session field: the session identifier travels inside the ZIP and in its filename.
 *
 * Browser-safe and dependency-free: the HTTP call is injected.
 */
import type { ValidationType } from "./types.ts";

/** Programs and their modules, as the server's enums list them (names are case-sensitive). */
export const TRAINING_PROGRAMS: Readonly<Record<string, readonly string[]>> = Object.freeze({
  waterway: ["Stonebrook", "Tidewall", "Fernwood", "Skyways", "Spaceways"],
  robo_arcade: ["Chef Says", "Myra mama", "Two Pans / Flip It"],
});
/** 25 MiB: the largest ZIP the server accepts. */
export const MAX_TRAINING_ZIP_BYTES = 26_214_400;
export const MAX_SUBJECT_ID = Number.MAX_SAFE_INTEGER;
const MAX_REPLY_BYTES = 65_536;
const RFC3339 = /^[0-9]{4}-[0-9]{2}-[0-9]{2}T[0-9]{2}:[0-9]{2}:[0-9]{2}(?:\.[0-9]{1,6})?(?:Z|[+-][0-9]{2}:[0-9]{2})$/;

export interface TrainingUploadInput {
  program: string;
  moduleName: string;
  moduleType: ValidationType;
  subjectId: number;
  /** Client time when the upload started, RFC 3339 (see `uploadTimestamp`). */
  uploadTimestamp: string;
  zip: Uint8Array;
  /** Basename the server keeps for downloads, e.g. "waterway_s101_S-20261104-1015-ab12.zip". */
  filename: string;
}

export interface TrainingUploadReceipt {
  id: string;
  status: "stored";
  validation_program: string;
  module_name: string;
  module_type: ValidationType;
  subject_id: number;
  filename: string;
  size_bytes: number;
  sha256: string;
  upload_timestamp: string;
  received_at: string;
}

export interface TrainingUploadResponse {
  httpStatus: number;
  receipt?: TrainingUploadReceipt;
  /** The server's error code, or INVALID_RESPONSE when the reply did not match the contract. */
  errorCode?: string;
  message?: string;
}

/** An upload that would break the API contract. It is never sent. */
export class TrainingUploadError extends Error {}
const fail = (message: string): never => { throw new TrainingUploadError(message); };

/** Formats epoch ms as an RFC 3339 UTC timestamp the API accepts, e.g. 2026-11-04T06:40:00.000Z. */
export function uploadTimestamp(ms: number): string {
  return new Date(ms).toISOString();
}

/** Checks every rule the API states for the six fields. Throws TrainingUploadError on the first one broken. */
export function checkTrainingUpload(input: TrainingUploadInput): void {
  const modules = Object.prototype.hasOwnProperty.call(TRAINING_PROGRAMS, input.program) ? TRAINING_PROGRAMS[input.program] : undefined;
  if (!modules) fail(`validation_program must be one of ${Object.keys(TRAINING_PROGRAMS).join(", ")}`);
  if (!modules!.includes(input.moduleName)) fail(`module_name "${input.moduleName}" is not a ${input.program} module (${modules!.join(", ")})`);
  if (input.moduleType !== "attention" && input.moduleType !== "memory") fail("module_type must be attention or memory");
  if (!Number.isSafeInteger(input.subjectId) || input.subjectId < 1) fail("subject_id must be a whole number from 1 to 9007199254740991");
  if (!RFC3339.test(input.uploadTimestamp) || Number.isNaN(Date.parse(input.uploadTimestamp))) fail("upload_timestamp must be an RFC 3339 timestamp");
  if (!(input.zip instanceof Uint8Array) || input.zip.length === 0) fail("data must be a non-empty ZIP");
  if (input.zip.length > MAX_TRAINING_ZIP_BYTES) fail(`The session ZIP is ${(input.zip.length / 1048576).toFixed(1)} MiB; the API accepts at most 25 MiB`);
  // Local file header signature: catches a non-ZIP before the server does.
  if (input.zip[0] !== 0x50 || input.zip[1] !== 0x4b) fail("data is not a ZIP archive");
  if (!/^[^/\\]+\.zip$/i.test(input.filename)) fail("filename must be a plain basename ending in .zip");
}

/** The multipart body: exactly the six fields, in the order the API lists them. */
export function buildTrainingUploadForm(input: TrainingUploadInput): FormData {
  checkTrainingUpload(input);
  const form = new FormData();
  form.append("validation_program", input.program);
  form.append("module_name", input.moduleName);
  form.append("module_type", input.moduleType);
  form.append("subject_id", String(input.subjectId));
  form.append("upload_timestamp", input.uploadTimestamp);
  form.append("data", new Blob([input.zip as BlobPart], { type: "application/zip" }), input.filename);
  return form;
}

const isObject = (v: unknown): v is Record<string, unknown> => !!v && typeof v === "object" && !Array.isArray(v);
const sameKeys = (o: Record<string, unknown>, keys: string[]) => {
  const k = Object.keys(o);
  return k.length === keys.length && keys.every((x) => k.includes(x));
};
const RECEIPT_KEYS = ["id", "status", "validation_program", "module_name", "module_type", "subject_id", "filename",
  "size_bytes", "sha256", "upload_timestamp", "received_at"];

/** Reads a reply strictly: anything outside the contract becomes INVALID_RESPONSE rather than a guess. */
export function parseTrainingUploadResponse(httpStatus: number, text: string): TrainingUploadResponse {
  const invalid: TrainingUploadResponse = { httpStatus, errorCode: "INVALID_RESPONSE", message: "The upload service returned an invalid response" };
  try {
    if (new TextEncoder().encode(text).length > MAX_REPLY_BYTES) return invalid;
    const root: unknown = JSON.parse(text);
    if (!isObject(root)) return invalid;
    if (httpStatus !== 200 && httpStatus !== 201) {
      const err = root.error;
      if (!sameKeys(root, ["error"]) || !isObject(err) || typeof err.code !== "string" || !err.code || typeof err.message !== "string") return invalid;
      return { httpStatus, errorCode: err.code.slice(0, 128), message: err.message.slice(0, 512) };
    }
    if (!sameKeys(root, RECEIPT_KEYS)) return invalid;
    const r = root as unknown as TrainingUploadReceipt;
    const ok = typeof r.id === "string" && r.status === "stored" && typeof r.validation_program === "string" &&
      typeof r.module_name === "string" && (r.module_type === "attention" || r.module_type === "memory") &&
      Number.isSafeInteger(r.subject_id) && r.subject_id >= 1 && typeof r.filename === "string" &&
      Number.isSafeInteger(r.size_bytes) && r.size_bytes >= 1 && r.size_bytes <= MAX_TRAINING_ZIP_BYTES &&
      typeof r.sha256 === "string" && /^[a-f0-9]{64}$/.test(r.sha256) &&
      typeof r.upload_timestamp === "string" && !Number.isNaN(Date.parse(r.upload_timestamp)) &&
      typeof r.received_at === "string" && !Number.isNaN(Date.parse(r.received_at));
    return ok ? { httpStatus, receipt: r } : invalid;
  } catch (e) {
    console.warn(`[upload] unreadable reply from the upload service (HTTP ${httpStatus}):`, e, text.slice(0, 500));
    return invalid;
  }
}

/** Whether the identical upload may be sent again: 408, 429, 5xx, or a 2xx whose receipt was unreadable. */
export function isUploadRetryable(r: TrainingUploadResponse): boolean {
  const s = r.httpStatus;
  return s === 408 || s === 429 || (s >= 500 && s <= 599) || ((s === 200 || s === 201) && !r.receipt);
}

/** Lowercase hex SHA-256 of the bytes (Web Crypto; available in browsers on secure origins and in Bun). */
export async function sha256Hex(bytes: Uint8Array): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", bytes as BufferSource);
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, "0")).join("");
}

/**
 * A receipt confirms this upload only if the server stored these exact bytes under this program, module,
 * type and subject. Returns the first mismatch, or null when it matches.
 */
export function receiptMismatch(receipt: TrainingUploadReceipt, input: TrainingUploadInput, sha256: string): string | null {
  if (receipt.sha256 !== sha256) return "checksum";
  if (receipt.size_bytes !== input.zip.length) return "size";
  if (receipt.subject_id !== input.subjectId) return "subject";
  if (receipt.module_name !== input.moduleName) return "module";
  if (receipt.module_type !== input.moduleType) return "module type";
  if (receipt.validation_program.replace(" ", "_") !== input.program) return "program";
  return null;
}

/** Sends a multipart body to the upload endpoint and returns the raw reply. */
export type TrainingUploadPost = (form: FormData) => Promise<{ status: number; text: string }>;

export interface TrainingUploadResult {
  response: TrainingUploadResponse;
  /** The receipt matched the upload (checksum, size, subject, module, type, program). */
  confirmed: boolean;
  retryable: boolean;
  /** Human-readable outcome for the operator. */
  message: string;
}

/** Uploads one session ZIP and verifies the receipt. Network failures come back as a retryable result. */
export async function uploadTrainingZip(input: TrainingUploadInput, post: TrainingUploadPost): Promise<TrainingUploadResult> {
  const form = buildTrainingUploadForm(input);
  const sha = await sha256Hex(input.zip);
  let response: TrainingUploadResponse;
  try {
    const reply = await post(form);
    response = parseTrainingUploadResponse(reply.status, reply.text);
  } catch (e) {
    const msg = (e instanceof Error ? e.message : String(e)).slice(0, 160);
    return { response: { httpStatus: 0, errorCode: "NETWORK", message: msg }, confirmed: false, retryable: true, message: `Upload failed: ${msg}` };
  }
  const rc = response.receipt;
  if (rc) {
    const mismatch = receiptMismatch(rc, input, sha);
    if (!mismatch) {
      const again = response.httpStatus === 200 ? " (already stored; original receipt)" : "";
      return { response, confirmed: true, retryable: false, message: `Stored as ${rc.id} for subject ${rc.subject_id}${again}.` };
    }
    return { response, confirmed: false, retryable: false, message: `The receipt did not match this upload (${mismatch}). The ZIP is kept locally.` };
  }
  const retryable = isUploadRetryable(response);
  return {
    response, confirmed: false, retryable,
    message: `HTTP ${response.httpStatus}${response.errorCode ? ` ${response.errorCode}` : ""}: ${response.message ?? "not stored"}`,
  };
}
